import { Button, Label } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { SelectProps } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';
import { alphaSort, noop } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { Link } from 'src/components/Link';
import * as copy from 'src/templates/copy';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';

type SelectableLinksProps = {
  allowEmpty?: boolean;
  buttonText: string;
  className?: string;
  creatable?: boolean;
  createNewItemRenderer?: (
    items: SelectItem[],
    testId?: string
  ) => SelectProps<SelectItem>['createNewItemRenderer'];
  createNewItemTestId?: string;
  createNewItemFromQuery?: SelectProps<SelectItem>['createNewItemFromQuery'];
  filterTestId?: string;
  header: string;
  highlightedValues?: Set<string>;
  items: SelectItem[];
  removeDisabledTooltip?: string;
  selected: SelectItem[];
  selectorTestId?: string;
  itemTestId?: (value: string, idx: number) => string | void;
  selectorItemTestId?: (value: string, idx?: number) => string | void;
  onClick?: (item: SelectItem) => void;
  onRemove?: (item: SelectItem) => void;
  onSelect?: (item: SelectItem | null) => void;
};

export const SelectableLinks: React.VFC<SelectableLinksProps> = ({
  allowEmpty = true,
  buttonText,
  className,
  creatable,
  createNewItemRenderer,
  createNewItemTestId,
  createNewItemFromQuery,
  filterTestId,
  header,
  highlightedValues,
  items,
  removeDisabledTooltip = '',
  selected,
  selectorTestId,
  itemTestId = noop,
  selectorItemTestId = noop,
  onClick,
  onRemove,
  onSelect = noop,
}) => {
  const selectMenu = (
    <ItemSelector.Single
      buttonText={buttonText}
      creatable={creatable}
      createNewItemFromQuery={createNewItemFromQuery}
      createNewItemRenderer={createNewItemRenderer}
      createNewItemTestId={createNewItemTestId}
      filterOrCreateText={copy.components.defaults.filterOrCreate}
      filterTestId={filterTestId}
      filterText={copy.components.defaults.filter}
      items={items}
      itemTestId={selectorItemTestId}
      onButtonClick={stopPropagation}
      onClear={stopPropagation}
      onSelect={onSelect}
      resetOnClose={creatable}
      selected={null}
      testId={selectorTestId}
    />
  );

  const removeDisabled = !allowEmpty && selected.length <= 1;

  const links = selected
    .sort(alphaSort(({ text }) => text))
    .map((item, idx) => (
      <div
        key={item.value}
        className={classNames(
          'sd-selectable-links__link',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted':
              highlightedValues && highlightedValues.has(item.value),
          }
        )}
        data-testid={itemTestId(item.value, idx)}
      >
        {onClick ? (
          <Link
            onClick={() => {
              onClick(item);
            }}
          >
            <Text ellipsize>{item.text}</Text>
          </Link>
        ) : (
          <Text ellipsize>{item.text}</Text>
        )}
        {onRemove ? (
          <Tooltip content={removeDisabledTooltip} disabled={!removeDisabled}>
            <Button
              disabled={removeDisabled}
              icon={IconNames.CROSS}
              minimal
              onClick={(evt: React.MouseEvent) => {
                stopPropagation(evt);
                onRemove(item);
              }}
            />
          </Tooltip>
        ) : null}
      </div>
    ));

  return (
    <div className={classNames('sd-selectable-links', className)}>
      <Label>
        {header}
        <div className="sd-selectable-links__menu">{selectMenu}</div>
      </Label>
      <div className="sd-selectable-links__list">{links}</div>
    </div>
  );
};
